import React, { useState, useEffect } from 'react';
import { Search, Download, Eye, RefreshCw, CreditCard, TrendingUp } from 'lucide-react';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7d');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setTimeout(() => {
      setPayments([
        {
          id: 'TXN001',
          customer: 'Jana Svobodová',
          provider: 'Praha Cleaning Service',
          service: 'Apartment Cleaning',
          amount: 1250.00,
          commission: 125.00,
          netAmount: 1125.00,
          status: 'completed',
          method: 'card',
          date: '2025-01-09',
          time: '14:30',
          transactionId: 'stripe_1234567890'
        },
        {
          id: 'TXN002',
          customer: 'Petr Novák',
          provider: 'Brno Plumbing Masters',
          service: 'Pipe Repair',
          amount: 2100.00,
          commission: 210.00,
          netAmount: 1890.00,
          status: 'pending',
          method: 'bank_transfer',
          date: '2025-01-09',
          time: '12:15',
          transactionId: 'bank_9876543210'
        },
        {
          id: 'TXN003',
          customer: 'Marie Černá',
          provider: 'Ostrava Electric Solutions',
          service: 'Electrical Installation',
          amount: 3500.00,
          commission: 350.00,
          netAmount: 3150.00,
          status: 'failed',
          method: 'card',
          date: '2025-01-08',
          time: '16:45',
          transactionId: 'stripe_5555666677',
          failureReason: 'Insufficient funds'
        },
        {
          id: 'TXN004',
          customer: 'Tomáš Dvořák',
          provider: 'Praha Cleaning Service',
          service: 'Office Cleaning',
          amount: 4200.00,
          commission: 420.00,
          netAmount: 3780.00,
          status: 'refunded',
          method: 'card',
          date: '2025-01-07',
          time: '10:20',
          transactionId: 'stripe_1111222233',
          refundAmount: 4200.00,
          refundDate: '2025-01-08'
        }
      ]);
      setLoading(false);
    }, 1000);
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const StatusBadge = ({ status }) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const PaymentMethodIcon = ({ method }) => {
    if (method === 'card') {
      return <CreditCard className="h-4 w-4 text-blue-600" />;
    }
    return <div className="h-4 w-4 bg-green-600 rounded text-white text-xs flex items-center justify-center">B</div>;
  };

  // Calculate summary stats
  const totalRevenue = filteredPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const totalCommission = filteredPayments.reduce((sum, payment) => sum + payment.commission, 0);
  const completedPayments = filteredPayments.filter(p => p.status === 'completed').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Management</h1>
          <p className="text-gray-600 mt-1">Track transactions, commissions, and payouts</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-2">
          <button className="btn-secondary flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button className="btn-primary flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-80">Total Revenue</p>
              <p className="text-2xl font-bold mt-1">{totalRevenue.toLocaleString()} Kč</p>
              <p className="text-sm mt-1 opacity-80">+12.5% from last month</p>
            </div>
            <TrendingUp className="h-8 w-8 opacity-80" />
          </div>
        </div>

        <div className="stat-card-green">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-80">Commission Earned</p>
              <p className="text-2xl font-bold mt-1">{totalCommission.toLocaleString()} Kč</p>
              <p className="text-sm mt-1 opacity-80">Platform revenue</p>
            </div>
            <CreditCard className="h-8 w-8 opacity-80" />
          </div>
        </div>

        <div className="stat-card-orange">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-80">Completed</p>
              <p className="text-2xl font-bold mt-1">{completedPayments}</p>
              <p className="text-sm mt-1 opacity-80">Successful transactions</p>
            </div>
            <div className="h-8 w-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-lg font-bold">✓</span>
            </div>
          </div>
        </div>

        <div className="stat-card-red">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-80">Failed/Pending</p>
              <p className="text-2xl font-bold mt-1">{filteredPayments.length - completedPayments}</p>
              <p className="text-sm mt-1 opacity-80">Need attention</p>
            </div>
            <div className="h-8 w-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-lg font-bold">!</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div className="sm:w-40">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Transaction</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Customer</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Provider</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Amount</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Commission</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{payment.id}</div>
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <PaymentMethodIcon method={payment.method} />
                          <span className="ml-2">{payment.method === 'card' ? 'Card' : 'Bank Transfer'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm text-gray-900">{payment.customer}</div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{payment.provider}</div>
                        <div className="text-xs text-gray-500">{payment.service}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm font-medium text-gray-900">{payment.amount.toLocaleString()} Kč</div>
                      {payment.refundAmount && (
                        <div className="text-xs text-red-600">Refunded: {payment.refundAmount.toLocaleString()} Kč</div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm text-gray-900">{payment.commission.toLocaleString()} Kč</div>
                      <div className="text-xs text-gray-500">10%</div>
                    </td>
                    <td className="py-4 px-4">
                      <StatusBadge status={payment.status} />
                      {payment.failureReason && (
                        <div className="text-xs text-red-600 mt-1">{payment.failureReason}</div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm text-gray-900">{payment.date}</div>
                      <div className="text-xs text-gray-500">{payment.time}</div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          title="Download Receipt"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payments;